/**
 * Component initializations
 */
import {MULTI_MEDIA_SOURCE_GETTY, MultiMediaUploadSource} from '@Libraries/user-media-library';
import type {Component, ComponentDefinitionDefined, ComponentModelDefinition, StyleProps, ToolbarButtonProps} from 'grapesjs';
import type {ImageData, StickerData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import type {MediaTypeModalProps} from '@Modals/media-type-modal/media-type-modal';
import {openMediaTypeModal} from '@Modals/media-type-modal/media-type-modal';
import {MediaType} from '@Panels/media-type-panel/media-type-panel';
import {GA4EventName} from '@Libraries/ga-events';
import {UserImageSource} from '@Libraries/user-image-library';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src/index';
import {ResponsiveType, TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {ImageItemSource, type ImageItemSourceType} from '@Libraries/image-item.library';

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const cmpt = editor.Components;
  const isEditorAiContentGenerationEnabled = $('#html-editor-container').attr('data-editor-ai-content-generation-enabled') === '1';

  const toolbarContainerBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      class: 'icon-select-parent size-icon-20 has-tooltip',
      title: window.i18next.t('pmwjs_select_container'),
      rel: 'tooltip',
    },
    label: '',
    events: {
      click: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
        $('.tooltip')?.remove();
        editor.runCommand('core:component-exit', {force: 1});
      },
      touchstart: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
        $('.tooltip')?.remove();
        editor.runCommand('core:component-exit', {force: 1});
      },
    },
  };

  const toolbarMoveBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      draggable: true,
      class: 'btn-tlb-move size-icon-20 icon-move gjs-no-touch-actions has-tooltip',
      title: window.i18next.t('pmwjs_move'),
      rel: 'tooltip',
    },
    command: 'tlb-move',
  };

  const toolbarMergeTagsBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      class: 'merge-tag-anchor gjs-no-touch-actions has-tooltip',
      title: window.i18next.t('pmwjs_add_merge_tag'),
      rel: 'tooltip',
    },
    events: {
      click: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
      },
      touchstart: (e: Event): void => {
        e.preventDefault();
        e.stopPropagation();
      },
    },
  };

  const toolbarAiRewriteBtn: Partial<ToolbarButtonProps> = {
    attributes: {
      class: 'gjs-no-touch-actions',
      title: window.i18next.t('pmwjs_rewrite_with_ai'),
      rel: 'tooltip',
      id: TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR,
    },
    events: {
      click: (e: Event): void => {
        // eslint-disable-next-line no-restricted-globals
        $('.tooltip')?.remove();
        e.preventDefault();
        e.stopPropagation();
      },
      touchstart: (e: Event): void => {
        // eslint-disable-next-line no-restricted-globals
        $('.tooltip')?.remove();
        e.stopPropagation();
      },
    },
  };

  const DEFAULT_TOOLBAR_OPTIONS: Partial<ToolbarButtonProps>[] = [toolbarContainerBtn, toolbarMoveBtn];

  const mediaHoverEventHandlers = {
    mouseenter: (event: MouseEvent): void => {
      const element = getMouseTargetedHTML(event);
      if (!element) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.TOGGLE_MEDIA_COMPONENT_OVERLAY, {element, show: true});
    },
    mouseleave: (event: MouseEvent): void => {
      const element = getMouseTargetedHTML(event);
      if (!element) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.TOGGLE_MEDIA_COMPONENT_OVERLAY, {element, show: false});
    },
    click: (event: MouseEvent): void => {
      const element = getMouseTargetedHTML(event);
      if (!element) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.TOGGLE_MEDIA_COMPONENT_OVERLAY, {element, show: false});
    },
  };

  const getDraggableForColumn = (): string => {
    return `[data-pmw-type="${window.PMW.emails.components.table.PMW_SECTION}"] [data-pmw-type="${window.PMW.emails.components.table.PMW_ROW}"]`;
  };

  const getStickerSource = (stickers: StickerData[]): ImageItemSourceType | undefined => {
    return stickers[0].type === ElementDataType.STILL_STICKER
      ? stickers[0].source
      : stickers[0].type === ElementDataType.EXTRACTED_GETTY_STICKER
        ? ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
        : undefined;
  };

  const isDroppableInRow = (target: Component, destination: Component): boolean => {
    return (target.get('type') === window.PMW.emails.components.table.PMW_CELL &&
      destination.get('type') === window.PMW.emails.components.table.PMW_ROW &&
      editor.runCommand(window.PMW.emails.commands.IS_COMPONENT_INSIDE_COMPONENT, {child: target, parent: destination})) as boolean;
  };

  const openTouchFriendlyMediaTypeModal = (props: MediaTypeModalProps): void => {
    setTimeout(() => {
      openMediaTypeModal(props);
    }, 50);
  };

  const stockMediaModelSelectionHandler = (imgs: ImageData[], component: Component): void => {
    if (imgs[0].source === UserImageSource.EMAIL_UPLOAD || imgs[0].source === UserImageSource.UPLOAD) {
      editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
        component: component,
        eventAction: GA4EventName.EMAILMAKER_UPLOAD_MEDIA,
      });
    }

    editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
      component: component,
      source: imgs[0].source,
      extension: imgs[0].extension,
      hashedFilename: imgs[0].hashedFilename,
    });
  };

  const isMainWrapperResized = (selected?: Component): boolean => {
    const mainWrapper: Component = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
    return selected === mainWrapper;
  };

  const mediaResizeOpts = {
    ratioDefault: true,
    step: 1,
    minDim: 10,
    maxDim: 100,
    unitWidth: '%',
    keepAutoHeight: true,
    keyWidth: 'width',
    currentUnit: true,
    tl: 1,
    tc: 0,
    tr: 1,
    cl: 0,
    cr: 0,
    bl: 1,
    bc: 0,
    br: 1,
    onEnd: (ev: Event, opts: {config: {unitWidth: number; keyWidth: string}; resizer: {rectDim: {w: string; h: string}}}): void => {
      const {config} = opts;
      const rect = opts.resizer.rectDim;
      const selected = editor.getSelected();
      const style = selected?.getStyle() ?? {};
      const activeDevice = editor.Devices.getSelected();
      const isTablet = activeDevice?.id === window.PMW.emails.DEVICE_TABLET;
      const isMobile = activeDevice?.id === window.PMW.emails.DEVICE_MOBILE;

      if (isMainWrapperResized(selected)) {
        return;
      }

      let cssPrefix = '';

      const {unitWidth} = config;
      let {keyWidth} = config;

      if (isTablet) {
        cssPrefix = window.PMW.emails.TABLET_CSS_PROP_PREFIX;
      } else if (isMobile) {
        cssPrefix = window.PMW.emails.MOBILE_CSS_PROP_PREFIX;
      }

      if (isTablet || isMobile) {
        keyWidth = cssPrefix + keyWidth;
        editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
          id: selected?.ccid,
          property: keyWidth,
          value: rect.w + unitWidth,
          deviceID: activeDevice.id,
          cssPrefix,
        });
      }

      style[keyWidth] = rect.w + unitWidth;
      selected?.setStyle(style);
      editor.runCommand(window.PMW.emails.commands.UPDATE_SLIDER_VALUE, {property: keyWidth, value: rect.w});
    },
  };

  const openChangeImageModal = (): void => {
    const component = editor.getSelected();

    if (!component) {
      return;
    }

    openTouchFriendlyMediaTypeModal({
      mediaType: MediaType.IMAGE,
      title: window.i18next.t('pmwjs_select_an_option'),
      onStartAddSelected: () => {
        editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
          component: component,
          loading: true,
          text: window.i18next.t('pmwjs_loading'),
        });
      },
      onAddSelected: (imgs: ImageData[]) => {
        stockMediaModelSelectionHandler(imgs, component);
      },
      hideRecordingOptions: true,
      uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
      maxSelectionNumber: 1,
      myDesignSelectedCallback: () => {
        editor.runCommand(window.PMW.emails.commands.OPEN_DESIGN_SELECTOR);
      },
      hideFooterSecondaryActions: true,
    });
  };

  const onTouchEnd = (e: TouchEvent): void => {
    // eslint-disable-next-line
    // @ts-ignore
    if (e.timeStamp - e.target.lastTouchEndEventTimeStamp < 400) {
      const event = new MouseEvent('dblclick');
      e.target?.dispatchEvent(event);
    }

    if (e.target) {
      // eslint-disable-next-line
      // @ts-ignore
      e.target.lastTouchEndEventTimeStamp = e.timeStamp;
    }
  };

  function onTextTouchEnd(this: ComponentModelDefinition, e: TouchEvent): void {
    if (this.model === editor.getEditing()) {
      e.stopPropagation();
      return;
    }

    // eslint-disable-next-line
    // @ts-ignore
    if (e.timeStamp - e.target.lastTouchEndEventTimeStamp < 400) {
      const event = new MouseEvent('dblclick');
      e.target?.dispatchEvent(event);
    }

    // eslint-disable-next-line
    // @ts-ignore
    e.target.lastTouchEndEventTimeStamp = e.timeStamp;
  }

  function getMouseTargetedHTML(event: MouseEvent): HTMLElement | null {
    const target = event.currentTarget;

    if (!target || !(target instanceof HTMLElement)) {
      return null;
    }

    return target;
  }

  cmpt.addType(window.PMW.emails.components.graphic.PMW_DESIGN, {
    extend: window.PMW.emails.components.default.IMAGE,
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'IMG' && el.getAttribute('data-is-pmw-design')) {
        return {
          type: window.PMW.emails.components.graphic.PMW_DESIGN,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
    model: {
      defaults: {
        trackingName: 'Design',
        name: window.i18next.t('pmwjs_my_design'),
        badgable: true,
        removable: true,
        highlightable: false,
        resizable: mediaResizeOpts,
        style: {
          width: '50%',
          height: 'auto',
          display: 'block',
          margin: 'auto',
          'max-width': '100%',
          padding: 0,
          opacity: '100%',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-radius': '0',
        },
        attributes: {
          'data-is-pmw-design': '1',
          'data-pmw-type': window.PMW.emails.components.graphic.PMW_DESIGN,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_my_design'),
          'aria-hidden': 'true',
        },
        src: window.PMW.util.asset_url('images/emailmaker/my_design_placeholder.png'),
        [window.PMW.emails.props.DESIGN_ID]: undefined,
        [window.PMW.emails.props.DESIGN_MODIFIED]: true,
        [window.PMW.emails.props.IS_INTERNAL]: false,
      },
      init() {
        editor.runCommand(window.PMW.emails.commands.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive(ev) {
        if (ev) {
          ev.stopPropagation();
          ev.preventDefault();
        }

        editor.runCommand(window.PMW.emails.commands.OPEN_DESIGN_SELECTOR, {model: this.model});
      },
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
  });

  cmpt.addType(window.PMW.emails.components.graphic.PMW_IMAGE, {
    extend: window.PMW.emails.components.default.IMAGE,
    model: {
      defaults: {
        trackingName: 'Image',
        resizable: mediaResizeOpts,
        removable: true,
        highlightable: false,
        name: window.i18next.t('pmwjs_image'),
        badgable: true,
        attributes: {
          'data-is-pmw-image': '1',
          'data-pmw-type': window.PMW.emails.components.graphic.PMW_IMAGE,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_image'),
          'aria-hidden': 'true',
        },
        src: window.PMW.util.asset_url('images/emailmaker/image_placeholder.png'),
        style: {
          width: '50%',
          '--mobile-width': '100%',
          'max-width': '100%',
          display: 'block',
          height: 'auto',
          'border-radius': '0',
          'border-color': '#FFFFFF',
          'border-width': '0px',
        },
        [window.PMW.emails.props.CONTENT_SOURCE]: '',
        [window.PMW.emails.props.CROP_DATA]: undefined,
        [window.PMW.emails.props.HASHED_FILENAME]: '',
        [window.PMW.emails.props.IS_MEDIA]: true,
        [window.PMW.emails.props.IS_CROPPABLE]: true,
        [window.PMW.emails.props.MEDIA_TYPE]: window.PMW.emails.mediaType.IMAGE,
      },
      init() {
        editor.runCommand(window.PMW.emails.commands.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
        editor.getModel().skip(() => {
          const source = this.get(window.PMW.emails.props.CONTENT_SOURCE) as string;
          const hashedFilename = this.get(window.PMW.emails.props.HASHED_FILENAME) as string;

          if (source === MULTI_MEDIA_SOURCE_GETTY) {
            editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
              source,
              component: this,
              hashedFilename,
            });
          }
        });
      },
    },
    view: {
      onActive(ev) {
        if (ev) {
          ev.stopPropagation();
          ev.preventDefault();
        }

        switch (this.model.get(window.PMW.emails.props.MEDIA_MODAL)) {
          case window.PMW.emails.mediaImageModal.MY_UPLOADS:
            editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_MY_UPLOADS_IMAGE, {
              component: this.model,
            });
            break;
          case window.PMW.emails.mediaImageModal.EXPLORE_MEDIA:
            editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_EXPLORE_MEDIA_IMAGE, {
              component: this.model,
            });
            break;
          case window.PMW.emails.mediaImageModal.AI_IMAGES:
            editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_AI_IMAGES, {
              component: this.model,
            });
            break;
          default:
            editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_ADD_IMAGE_OPTION_SELECTOR, {
              component: this.model,
            });
        }
      },
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: openChangeImageModal,
        touchend: onTouchEnd,
        ...mediaHoverEventHandlers,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'IMG' && el.getAttribute('data-is-pmw-image')) {
        return {
          type: window.PMW.emails.components.graphic.PMW_IMAGE,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.graphic.PMW_VIDEO, {
    extend: window.PMW.emails.components.graphic.PMW_IMAGE,
    model: {
      defaults: {
        trackingName: 'Video',
        name: window.i18next.t('pmwjs_video'),
        attributes: {
          'data-is-pmw-video': '1',
          'data-pmw-type': window.PMW.emails.components.graphic.PMW_VIDEO,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_video'),
          'aria-hidden': 'true',
        },
        style: {
          'border-radius': '0',
          display: 'block',
          height: 'auto',
          width: '50%',
          '--mobile-width': '100%',
          'max-width': '100%',
        },
        src: window.PMW.util.asset_url('images/emailmaker/video_placeholder.png'),
        [window.PMW.emails.props.MEDIA_TYPE]: window.PMW.emails.mediaType.VIDEO,
      },
      init() {
        editor.runCommand(window.PMW.emails.commands.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive(ev) {
        if (ev) {
          ev.stopPropagation();
          ev.preventDefault();
        }

        editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_ADD_VIDEO_OPTION_SELECTOR, {
          component: this.model,
        });
      },
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'IMG' && el.getAttribute('data-is-pmw-video')) {
        return {
          type: window.PMW.emails.components.graphic.PMW_VIDEO,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.graphic.PMW_STICKER, {
    extend: window.PMW.emails.components.graphic.PMW_IMAGE,
    model: {
      defaults: {
        trackingName: 'Sticker',
        name: window.i18next.t('pmwjs_sticker'),
        attributes: {
          'data-is-pmw-sticker': '1',
          'data-pmw-type': window.PMW.emails.components.graphic.PMW_STICKER,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_sticker'),
          'aria-hidden': 'true',
        },
        style: {
          'border-radius': '0',
          width: '50%',
          '--mobile-width': '100%',
          'max-width': '100%',
          display: 'block',
          height: 'auto',
        },
        src: window.PMW.util.asset_url('images/emailmaker/sticker_placeholder.png'),
        [window.PMW.emails.props.MEDIA_TYPE]: window.PMW.emails.mediaType.STILL_STICKER,
      },
      init() {
        editor.runCommand(window.PMW.emails.commands.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive() {
        editor.runCommand(window.PMW.emails.commands.OPEN_STICKER_MODAL, {
          onStartAddSelected: () => {
            editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
              component: this.model,
              loading: true,
              text: window.i18next.t('pmwjs_loading'),
            });
          },
          onAddSelected: (stickers: StickerData[]) => {
            this.model.set(
              window.PMW.emails.props.MEDIA_TYPE,
              stickers[0].type === ElementDataType.ANIMATED_STICKER ? window.PMW.emails.mediaType.ANIMATED_STICKER : window.PMW.emails.mediaType.STILL_STICKER
            );

            const options = {
              component: this.model,
              source: getStickerSource(stickers),
              extension: undefined,
              hashedFilename: stickers[0].hashedFilename,
            };

            editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, options);
          },
        });
      },
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'IMG' && el.getAttribute('data-is-pmw-sticker')) {
        return {
          type: window.PMW.emails.components.graphic.PMW_STICKER,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.text.PMW_LINK, {
    tagName: 'a',
    model: {
      defaults: {
        trackingName: 'Link',
        selectable: false,
        hoverable: false,
        highlightable: false,
        draggable: false,
        droppable: false,
        toolbar: [toolbarContainerBtn],
        attributes: {
          'data-is-pmw-link': '1',
          'data-pmw-type': window.PMW.emails.components.text.PMW_LINK,
        },
        style: {
          'text-decoration': 'inherit',
          'border-radius': '0',
        },
      },
    },
    view: {
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'A' && !el.getAttribute('data-is-pmw-social-item')) {
        return {
          type: window.PMW.emails.components.text.PMW_LINK,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.social.PMW_SOCIALS, {
    tagName: 'tr',
    model: {
      defaults: {
        trackingName: 'Socials',
        draggable: true,
        hoverable: true,
        selectable: true,
        highlightable: false,
        droppable: false,
        name: window.i18next.t('pmwjs_social'),
        badgable: true,
        attributes: {
          'data-is-pmw-socials': '1',
          'data-pmw-type': window.PMW.emails.components.social.PMW_SOCIALS,
        },
        style: {
          display: 'block',
          margin: '0',
          width: 'unset',
          'min-height': 'fit-content',
          padding: '8px',
          'text-align': 'center',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-style': 'none',
        },
      },
      init() {
        this.on('change:style:width', () => {
          if (this.getStyle().width === '100%') {
            editor.runCommand(window.PMW.emails.commands.FIX_COMPONENT_FULL_WIDTH_OVERFLOW_AOL_YAHOO);
          }
        });
      },
    },
    view: {
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'DIV' && el.getAttribute('data-is-pmw-socials')) {
        return {
          type: window.PMW.emails.components.social.PMW_SOCIALS,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.social.PMW_SOCIALS_ITEM, {
    tagName: 'a',
    model: {
      defaults: {
        trackingName: null,
        name: window.i18next.t('pmwjs_social_link'),
        toolbar: [],
        removable: false,
        selectable: false,
        hoverable: false,
        highlightable: false,
        badgable: false,
        draggable: false,
        droppable: false,
        attributes: {
          'data-is-pmw-social-item': '1',
          'data-pmw-type': window.PMW.emails.components.social.PMW_SOCIALS_ITEM,
        },
        style: {
          margin: '5px',
          'text-decoration': 'none',
          display: 'inline-block',
        },
      },
    },
    view: {
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'A' && el.getAttribute('data-is-pmw-social-item')) {
        return {
          type: window.PMW.emails.components.social.PMW_SOCIALS_ITEM,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.social.PMW_SOCIALS_IMAGE, {
    extend: window.PMW.emails.components.default.IMAGE,
    model: {
      defaults: {
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        hoverable: false,
        resizable: false,
        badgable: false,
        draggable: false,
        droppable: false,
        editable: false,
        attributes: {
          'data-is-pmw-social-image': '1',
          'data-pmw-type': window.PMW.emails.components.social.PMW_SOCIALS_IMAGE,
          alt: '',
        },
        style: {
          width: '38px',
          height: '38px',
          display: 'inline-block',
        },
      },
    },
    view: {
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'IMG' && el.getAttribute('data-is-pmw-social-image')) {
        return {
          type: window.PMW.emails.components.social.PMW_SOCIALS_IMAGE,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.table.PMW_TABLE, {
    extend: window.PMW.emails.components.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Table',
        removable: true,
        selectable: true,
        highlightable: false,
        name: window.i18next.t('pmwjs_table'),
        badgable: true,
        draggable: [`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT_CONTAINER}"]`],
        droppable: false,
        [window.PMW.emails.props.HIERARCHY_ROOT]: true,
        [window.PMW.emails.props.HIERARCHY_SELECTION]: true,
        propagate: [window.PMW.emails.props.HIERARCHY_SELECTION],
        attributes: {
          'data-is-pmw-table': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_TABLE,
          cellspacing: 1,
        },
        components: [
          {
            type: window.PMW.emails.components.table.PMW_TBODY,
            components: [
              {
                tagName: 'tr',
                type: window.PMW.emails.components.table.PMW_ROW,
                components: [{type: window.PMW.emails.components.table.PMW_CELL}, {type: window.PMW.emails.components.table.PMW_CELL}],
              },
              {
                tagName: 'tr',
                type: window.PMW.emails.components.table.PMW_ROW,
                components: [{type: window.PMW.emails.components.table.PMW_CELL}, {type: window.PMW.emails.components.table.PMW_CELL}],
              },
            ],
          },
        ],
        style: {
          padding: '6px',
          margin: '0px',
          width: '100%',
          'vertical-align': 'middle',
          height: 'auto',
          'font-family': 'Helvetica, sans-serif',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position': 'center',
          'background-color': 'transparent',
          'border-color': '#000000',
          'border-radius': '0',
          opacity: '100%',
          'pmw-rows': 2,
          'pmw-columns': 2,
          'pmw-cell-border-width': '0px',
          'pmw-cell-border-color': '#242424',
          'pmw-cell-border-radius': '0px',
          'pmw-cell-background-color': 'transparent',
          'pmw-cell-spacing': 1,
        },
      },
      init() {
        this.components().forEach((cell) => {
          editor.runCommand(window.PMW.emails.commands.APPLY_GLOBAL_CELL_STYLES, {parent: this, cell});
        });

        editor.runCommand(window.PMW.emails.commands.ADD_COPY_TABLE_STYLE_TO_TABLE_BODY_LISTENER, {component: this, stylesToCopy: ['background-color']});
      },
    },
    view: {
      onRender({model}) {
        editor.runCommand(window.PMW.emails.commands.HANDLE_TABLE_PADDING, {
          component: model,
        });
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TABLE' && el.getAttribute('data-is-pmw-table')) {
        return {
          type: window.PMW.emails.components.table.PMW_TABLE,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.table.PMW_SECTION, {
    extend: window.PMW.emails.components.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Section',
        removable: true,
        selectable: true,
        highlightable: false,
        name: window.i18next.t('pmwjs_section'),
        badgable: true,
        draggable: [`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT_CONTAINER}"]`],
        droppable: false,
        responsive: true,
        [window.PMW.emails.props.COLUMNS_PRESET]: 1,
        [window.PMW.emails.props.APPLY_INITIAL_PRESET]: false,
        [window.PMW.emails.props.HIERARCHY_ROOT]: true,
        [window.PMW.emails.props.HIERARCHY_SELECTION]: true,
        [window.PMW.emails.props.IS_RESPONSIVE_MOBILE]: true,
        [window.PMW.emails.props.IS_RESPONSIVE_TABLET]: false,
        propagate: [window.PMW.emails.props.HIERARCHY_SELECTION],
        attributes: {
          'data-is-pmw-section': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_SECTION,
          cellspacing: '0',
          cellpadding: '15',
          class: 'pmw-responsive-table',
        },
        components: [
          {
            type: window.PMW.emails.components.table.PMW_TBODY,
            components: [
              {
                tagName: 'tr',
                type: window.PMW.emails.components.table.PMW_ROW,
                components: [
                  {
                    type: window.PMW.emails.components.table.PMW_CELL,
                    hoverable: true,
                    selectable: true,
                    highlightable: true,
                  },
                ],
              },
            ],
          },
        ],
        style: {
          padding: '15px',
          margin: '0px',
          width: '100%',
          'vertical-align': 'middle',
          height: 'auto',
          'font-family': 'Helvetica, sans-serif',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position': 'center',
          'background-color': 'transparent',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-radius': '0',
          opacity: '100%',
        },
      },
      init() {
        if (typeof this.get(window.PMW.emails.props.IS_RESPONSIVE_TABLET) === 'undefined' || this.get(window.PMW.emails.props.IS_RESPONSIVE_TABLET) === true) {
          editor.runCommand(window.PMW.emails.commands.UPDATE_SECTION_RESPONSIVE, {
            component: this,
            responsive: true,
            type: ResponsiveType.TABLET,
          });
        }

        const component = this;
        this.listenTo(this, 'change:style', () => {
          editor.runCommand(window.PMW.emails.commands.SYNC_TABLE_TBODY_COLORS, {
            component: component,
          });
        });

        editor.runCommand(window.PMW.emails.commands.ADD_COPY_TABLE_STYLE_TO_TABLE_BODY_LISTENER, {component: this, stylesToCopy: ['background-color', 'border-radius']});
      },
    },
    view: {
      onRender({model}) {
        const presetID = Number(model.get(window.PMW.emails.props.COLUMNS_PRESET));
        const applyPreset = Boolean(model.get(window.PMW.emails.props.APPLY_INITIAL_PRESET));

        if (applyPreset && presetID) {
          editor.runCommand(window.PMW.emails.commands.UPDATE_COLUMNS_PRESET, {
            component: model,
            presetID,
          });
          model.set(window.PMW.emails.props.APPLY_INITIAL_PRESET, false);
        }

        editor.runCommand(window.PMW.emails.commands.HANDLE_TABLE_PADDING, {
          component: model,
        });

        editor.runCommand(window.PMW.emails.commands.SYNC_TABLE_TBODY_COLORS, {
          component: model,
        });
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TABLE' && el.getAttribute('data-is-pmw-section')) {
        return {
          type: window.PMW.emails.components.table.PMW_SECTION,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.misc.PMW_DIVIDER, {
    extend: window.PMW.emails.components.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Divider',
        tagName: 'table',
        highlightable: true,
        droppable: false,
        draggable: true,
        hoverable: true,
        editable: true,
        selectable: true,
        deletable: true,
        badgable: true,
        name: window.i18next.t('pmwjs_divider'),
        icon: 'icon-divider',
        attributes: {
          'data-pmw-type': window.PMW.emails.components.misc.PMW_DIVIDER,
          'data-is-pmw-divider': 1,
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          width: '100%',
          height: window.PMW.emails.DIVIDER_DEFAULT_HEIGHT,
          border: '0',
          'border-radius': '0',
          margin: '0',
          outline: 'none',
          background: 'none',
          color: 'transparent',
          'background-color': 'transparent',
        },
        components: [
          {
            tagName: 'tr',
            type: window.PMW.emails.components.table.PMW_ROW,
            components: [
              {
                type: window.PMW.emails.components.misc.PMW_DIVIDER_CELL,
                style: {
                  width: '100%',
                  height: window.PMW.emails.DIVIDER_DEFAULT_HEIGHT,
                  'background-color': 'transparent',
                },
              },
            ],
          },
        ],
      },
      init() {
        const styles = this.getStyle();

        if (styles && typeof styles.width === 'undefined') {
          styles.width = '100%';
          this.setStyle(styles);
        }

        this.setStyle({...this.defaults.style, ...this.getStyle()} as StyleProps);
      },
    },
    view: {
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TABLE' && el.getAttribute('data-is-pmw-divider')) {
        return {
          type: window.PMW.emails.components.misc.PMW_DIVIDER,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.misc.PMW_DIVIDER_LEGACY, {
    model: {
      defaults: {
        trackingName: 'Divider',
        tagName: 'hr',
        highlightable: false,
        droppable: false,
        hoverable: true,
        name: window.i18next.t('pmwjs_divider'),
        badgable: true,
        attributes: {
          'data-pmw-type': window.PMW.emails.components.misc.PMW_DIVIDER_LEGACY,
        },
        style: {
          height: '30px',
          border: '0',
          'border-radius': '0',
          margin: '0',
        },
      },
    },
    view: {
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: onTouchEnd,
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'HR') {
        return {
          type: window.PMW.emails.components.misc.PMW_DIVIDER_LEGACY,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  const buttonToolbarOptions = [toolbarMergeTagsBtn, ...DEFAULT_TOOLBAR_OPTIONS];
  if (isEditorAiContentGenerationEnabled) {
    buttonToolbarOptions.unshift(toolbarAiRewriteBtn);
  }

  cmpt.addType(window.PMW.emails.components.text.PMW_BUTTON, {
    extend: window.PMW.emails.components.default.LINK,
    model: {
      defaults: {
        trackingName: 'Button',
        toolbar: buttonToolbarOptions,
        removable: true,
        highlightable: true,
        hoverable: true,
        selectable: true,
        droppable: false,
        draggable: true,
        editable: false,
        name: window.i18next.t('pmwjs_button'),
        badgable: true,
        attributes: {
          'data-is-pmw-button': '1',
          'data-pmw-type': window.PMW.emails.components.text.PMW_BUTTON,
          class: 'pmw-content-button',
        },
        components: [editor.runCommand(window.PMW.emails.commands.GET_TABLE_COMPONENT_FOR_BUTTON)],
        style: {
          'background-color': '#FD595B',
          'border-radius': '12px',
          color: '#FFFFFF',
          padding: '15px',
          'font-size': '16px',
          'letter-spacing': 'normal',
          'line-height': '20px',
          margin: '0 auto 0 auto',
          width: 'auto',
          'white-space': 'break-spaces',
          'vertical-align': 'middle',
          'text-align': 'center',
          display: 'table',
          'font-family': 'Helvetica, sans-serif',
          opacity: '100%',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-style': 'none',
          'font-style': 'normal',
          'font-weight': 'normal',
          'text-decoration': 'none',
        },
        [window.PMW.emails.props.COMPONENT_PRESET]: window.PMW.emails.BUTTON_DEFAULT_PRESET_ID,
      },
      init() {
        if (this.get(window.PMW.emails.props.COMPONENT_PRESET) === window.PMW.emails.BUTTON_DEFAULT_PRESET_ID) {
          const style = this.getStyle();

          if (typeof style['border-radius'] !== 'undefined') {
            const radius = style['border-radius'] as string;

            if (radius) {
              const radiusNum = parseInt(radius, 10);

              if (radiusNum === window.PMW.emails.buttonPresets[1]) {
                this.set(window.PMW.emails.props.COMPONENT_PRESET, 1);
              } else if (radiusNum <= window.PMW.emails.buttonPresets[2]) {
                this.set(window.PMW.emails.props.COMPONENT_PRESET, 2);
              } else if (radiusNum <= window.PMW.emails.buttonPresets[3]) {
                this.set(window.PMW.emails.props.COMPONENT_PRESET, 3);
              } else {
                this.set(window.PMW.emails.props.COMPONENT_PRESET, 4);
              }
            }
          }
        }

        const style = this.getStyle();
        const width = style.width === 'fit-content' ? 'auto' : style.width;

        this.setStyle({...this.defaults.style, ...style, display: 'table', width} as StyleProps);

        this.on('change:style:width', () => {
          if (this.getStyle().width === '100%') {
            editor.runCommand(window.PMW.emails.commands.FIX_COMPONENT_FULL_WIDTH_OVERFLOW_AOL_YAHOO);
          }
        });
      },
    },
    view: {
      onTouchEnd: onTextTouchEnd,
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: 'onTouchEnd',
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'A' && el.getAttribute('data-is-pmw-button')) {
        return {
          type: window.PMW.emails.components.text.PMW_BUTTON,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  const textToolbarOptions = [toolbarMergeTagsBtn, ...DEFAULT_TOOLBAR_OPTIONS];
  if (isEditorAiContentGenerationEnabled) {
    textToolbarOptions.unshift(toolbarAiRewriteBtn);
  }

  cmpt.addType(window.PMW.emails.components.text.PMW_TEXT, {
    extend: window.PMW.emails.components.default.TEXT,
    model: {
      defaults: {
        trackingName: 'Text',
        tagName: 'p',
        highlightable: false,
        hoverable: true,
        name: window.i18next.t('pmwjs_text'),
        badgable: true,
        droppable: false,
        toolbar: textToolbarOptions,
        content: window.i18next.t('pmwjs_insert_text'),
        attributes: {
          'data-pmw-type': window.PMW.emails.components.text.PMW_TEXT,
        },
        style: {
          color: '#242424',
          display: 'block',
          padding: '10px',
          margin: '0',
          width: 'unset',
          'box-sizing': 'border-box',
          'word-break': 'break-word',
          'overflow-wrap': 'break-word',
          'letter-spacing': 'normal',
          'font-size': '16px',
          'font-weight': 'normal',
          'line-height': '20px',
          'font-family': 'Helvetica, sans-serif',
          'text-align': 'left',
          opacity: '100%',
          'border-color': '#FFFFFF',
          'border-width': '0px',
        },
      },
    },
    view: {
      onTouchEnd: onTextTouchEnd,
      events: {
        // eslint-disable-next-line
        // @ts-ignore
        dblclick: 'onActive',
        touchend: 'onTouchEnd',
      },
    },
    isComponent: (el) => {
      if (el instanceof Element && el.getAttribute('data-is-readonly-text')) {
        return false;
      }

      const validTags = ['P', 'SPAN', 'DIV', 'OL', 'UL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LABEL', 'PRE', 'EM'];

      if (validTags.includes(el.tagName)) {
        const contentTexts: string[] = [];

        Array.from(el.childNodes).forEach((node) => {
          if (node.textContent && node.textContent !== '') {
            contentTexts.push(node.textContent);
          }
        });

        const content = contentTexts.join('<br/>');

        return {
          type: window.PMW.emails.components.text.PMW_TEXT,
          content,
          components: [],
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.table.PMW_TBODY, {
    extend: window.PMW.emails.components.default.TBODY,
    model: {
      defaults: {
        tagName: 'tbody',
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        badgable: false,
        draggable: false,
        hoverable: false,
        attributes: {
          'data-is-pmw-tbody': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_TBODY,
        },
      },
      style: {
        'background-color': 'transparent',
        height: '100%',
        width: '100%',
        background: 'none',
        margin: '0',
        opacty: '100%',
        border: 'none',
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TBODY') {
        return {
          type: window.PMW.emails.components.table.PMW_TBODY,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.table.PMW_ROW, {
    extend: window.PMW.emails.components.default.ROW,
    model: {
      defaults: {
        tagName: 'tr',
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        badgable: false,
        droppable: isDroppableInRow,
        draggable: false,
        hoverable: false,
        attributes: {
          'data-is-pmw-row': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_ROW,
        },
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TR' && el.getAttribute('data-is-pmw-row')) {
        return {
          type: window.PMW.emails.components.table.PMW_ROW,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.table.PMW_CELL, {
    extend: window.PMW.emails.components.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: 'Cell',
        removable: false,
        name: window.i18next.t('pmwjs_column'),
        badgable: true,
        draggable: getDraggableForColumn(),
        droppable: true,
        copyable: false,
        attributes: {
          'data-is-pmw-cell': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_CELL,
          class: 'pmw-responsive-cell',
        },
        style: {
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position': 'center',
          'background-color': 'transparent',
          margin: '0px',
          'min-width': '10px',
          padding: '30px',
          width: 'auto',
        },
        components: [
          {
            type: window.PMW.emails.components.text.PMW_TEXT,
          },
        ],
      },
      init() {
        const isTableParent = this.parent()?.parent()?.is(window.PMW.emails.components.table.PMW_TABLE);
        const name = isTableParent ? window.i18next.t('pmwjs_cell') : window.i18next.t('pmwjs_column');
        this.set('name', name);
      },
    },
    view: {
      onRender({model}) {
        editor.runCommand(window.PMW.emails.commands.HANDLE_TABLE_PADDING, {
          component: model,
        });
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TD' && el.getAttribute('data-is-pmw-cell')) {
        return {
          type: window.PMW.emails.components.table.PMW_CELL,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.table.PMW_STATIC_CELL, {
    extend: window.PMW.emails.components.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: null,
        toolbar: [],
        removable: true,
        selectable: true,
        highlightable: false,
        badgable: false,
        draggable: true,
        hoverable: true,
        stylable: true,
        attributes: {
          'data-is-pmw-static-cell': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_STATIC_CELL,
        },
        style: {
          padding: '0px',
          margin: '0px',
          'min-width': '10px',
        },
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TD' && !el.getAttribute('data-is-pmw-cell')) {
        return {
          type: window.PMW.emails.components.table.PMW_STATIC_CELL,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.misc.PMW_DIV, {
    extend: window.PMW.emails.components.default.WRAPPER,
    model: {
      defaults: {
        tagName: 'div',
        trackingName: null,
        removable: true,
        selectable: true,
        highlightable: false,
        badgable: false,
        draggable: true,
        droppable: true,
        hoverable: true,
        stylable: true,
        attributes: {
          'data-is-postermywall-div': '1',
          'data-pmw-type': window.PMW.emails.components.misc.PMW_DIV,
        },
        style: {
          padding: '0px',
          margin: '0px',
          'min-width': '10px',
        },
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'DIV' && !el.getAttribute('data-is-pmw-wrapper')) {
        return {
          type: window.PMW.emails.components.misc.PMW_DIV,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.container.PMW_WRAPPER, {
    extend: window.PMW.emails.components.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Email',
        toolbar: [],
        removable: false,
        highlightable: false,
        hoverable: false,
        badgable: false,
        copyable: false,
        draggable: [window.PMW.emails.components.default.WRAPPER],
        droppable: false,
        attributes: {
          'data-is-pmw-wrapper': '1',
          'data-pmw-type': window.PMW.emails.components.container.PMW_WRAPPER,
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          'background-color': '#FAFAFA', // neutral-1
          height: '100% !important',
          width: '100% !important',
        },
        [window.PMW.emails.props.TEMPLATE_NAME]: window.PMW.emails.NEW_TEMPLATE_NAME,
        components: [
          {
            type: window.PMW.emails.components.table.PMW_TBODY,
            components: [
              {
                type: window.PMW.emails.components.table.PMW_ROW,
                style: {
                  height: '100%',
                },
                components: [
                  {
                    type: window.PMW.emails.components.container.PMW_WRAPPER_CELL,
                    style: {
                      height: '100%',
                    },
                    stylable: false,
                    removable: false,
                    selectable: false,
                    hoverable: false,
                    badgable: false,
                    components: [
                      {
                        type: window.PMW.emails.components.container.PMW_CONTENT,
                        components: [
                          {
                            type: window.PMW.emails.components.table.PMW_TBODY,
                            components: [
                              {
                                type: window.PMW.emails.components.table.PMW_ROW,
                                components: [
                                  {
                                    type: window.PMW.emails.components.container.PMW_CONTENT_CONTAINER,
                                    stylable: false,
                                    removable: false,
                                    selectable: false,
                                    hoverable: false,
                                    badgable: false,
                                    droppable: true,
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TABLE' && el.getAttribute('data-is-pmw-wrapper')) {
        return {
          type: window.PMW.emails.components.container.PMW_WRAPPER,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.container.PMW_CONTENT_CONTAINER, {
    extend: window.PMW.emails.components.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: null,
        toolbar: [],
        removable: false,
        highlightable: false,
        hoverable: false,
        badgable: false,
        copyable: false,
        draggable: false,
        droppable: true,
        stylable: false,
        attributes: {
          'data-pmw-type': window.PMW.emails.components.container.PMW_CONTENT_CONTAINER,
          'data-is-pmw-content-container': 1,
          align: 'middle',
        },
        style: {
          background: 'none',
          width: '100%',
          margin: '0',
          padding: '0',
          height: '100%',
          'min-height': '100%',
          'max-height': 'none',
          'vertical-align': 'top',
        },
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TABLE' && el.getAttribute('data-is-pmw-content-container')) {
        return {
          type: window.PMW.emails.components.container.PMW_CONTENT_CONTAINER,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.container.PMW_CONTENT, {
    extend: window.PMW.emails.components.default.TABLE,
    model: {
      defaults: {
        tagName: 'table',
        trackingName: 'Content',
        toolbar: [],
        removable: false,
        highlightable: false,
        selectable: false,
        hoverable: false,
        badgable: false,
        copyable: false,
        draggable: [window.PMW.emails.components.container.PMW_CONTENT_CONTAINER],
        droppable: false,
        stylable: true,
        attributes: {
          'data-is-pmw-content': '1',
          'data-pmw-type': window.PMW.emails.components.container.PMW_CONTENT,
          class: 'pmw-content-container',
          cellspacing: 0,
        },
        gettyImageCount: 0,
        gettyVideoCount: 0,
        style: {
          'background-color': '#FFFFFF',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'font-family': 'Helvetica, sans-serif',
          'margin-left': 'auto',
          'margin-right': 'auto',
          height: '100%',
          'min-width': 'min(600px, 100%)',
          'min-height': '100%',
          'max-width': window.PMW.emails.MAX_CONTENT_WIDTH,
          'max-height': 'none',
          'vertical-align': 'top',
          width: '80%',
        },
      },
    },
    view: {
      onRender({model}) {
        editor.runCommand(window.PMW.emails.commands.HANDLE_TABLE_PADDING, {
          component: model,
        });

        editor.runCommand(window.PMW.emails.commands.UPDATE_CONTENT_MAX_WIDTH, {
          component: model,
        });
      },
      init() {
        const component = this;
        this.listenTo(this, 'change:style', () => {
          editor.runCommand(window.PMW.emails.commands.SYNC_TABLE_TBODY_COLORS, {
            component: component,
          });
        });
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TABLE' && el.getAttribute('data-is-pmw-content')) {
        return {
          type: window.PMW.emails.components.container.PMW_CONTENT,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.container.PMW_WRAPPER_CELL, {
    extend: window.PMW.emails.components.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        hoverable: false,
        badgable: false,
        draggable: [window.PMW.emails.components.default.WRAPPER],
        droppable: false,
        stylable: false,
        attributes: {
          'data-is-pmw-wrapper-cell': '1',
          'data-pmw-type': window.PMW.emails.components.container.PMW_WRAPPER_CELL,
          cellspacing: 0,
          align: 'middle',
        },
        style: {
          background: 'none',
          width: '100%',
          margin: 'auto',
          height: 'auto',
          'min-height': '100%',
          'max-height': 'none',
          'font-family': 'Helvetica, sans-serif',
          border: 'none',
          'vertical-align': 'top',
        },
      },
    },
    isComponent: (el) => {
      if (el.tagName && el.tagName === 'TD' && el.getAttribute('data-is-pmw-wrapper-cell')) {
        return {
          type: window.PMW.emails.components.container.PMW_WRAPPER_CELL,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.default.TEXT, {
    model: {
      defaults: {
        trackingName: null,
        highlightable: false,
        hoverable: false,
        selectable: false,
        badgable: false,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
      },
    },
    isComponent: (el) => {
      if (el instanceof Element) {
        const tagName = el.tagName.toLowerCase();

        if (window.PMW.emails.RTE_TAGNAMES.includes(tagName)) {
          return {
            type: window.PMW.emails.components.default.TEXT,
          } as ComponentDefinitionDefined;
        }
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.default.DEFAULT, {
    model: {
      defaults: {
        trackingName: null,
        copyable: false,
        draggable: false,
        droppable: false,
        hoverable: false,
        selectable: false,
        highlightable: false,
      },
    },
  });

  cmpt.addType(window.PMW.emails.components.default.WRAPPER, {
    model: {
      defaults: {
        trackingName: null,
        copyable: false,
        draggable: false,
        droppable: false,
        hoverable: false,
        selectable: false,
        highlightable: false,
      },
    },
  });

  cmpt.addType(window.PMW.emails.components.default.TBODY, {
    model: {
      defaults: {
        trackingName: null,
        copyable: false,
        draggable: false,
        droppable: false,
        hoverable: false,
        selectable: false,
        highlightable: false,
        style: {
          background: 'none',
          width: '100%',
          margin: '0',
          height: 'auto',
          opacty: '100%',
          border: 'none',
        },
      },
    },
  });

  cmpt.addType(window.PMW.emails.components.misc.PMW_DIVIDER_CELL, {
    extend: window.PMW.emails.components.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        deletable: false,
        highlightable: false,
        hoverable: false,
        selectable: false,
        badgable: false,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
        trackingName: null,
        toolbar: [],
        attributes: {
          'data-pmw-type': window.PMW.emails.components.misc.PMW_DIVIDER_CELL,
          'data-is-pmw-divider-cell': 1,
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          width: '100%',
          height: '100%',
        },
      },
    },
    view: {
      onRender({model}) {
        editor.runCommand(window.PMW.emails.commands.REMOVE_BUGGY_BACKGROUND_ATTRIBUTE, {
          component: model,
        });
      },
    },
    isComponent: (el) => {
      if (el instanceof Element && el.getAttribute('data-is-pmw-divider-cell')) {
        return {
          type: window.PMW.emails.components.misc.PMW_DIVIDER_CELL,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  cmpt.addType(window.PMW.emails.components.misc.PMW_READ_ONLY_FOOTER, {
    model: {
      defaults: {
        name: window.i18next.t('pmwjs_footer'),
        trackingName: null,
        deletable: false,
        highlightable: true,
        hoverable: true,
        selectable: true,
        badgable: true,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
        toolbar: [],
        attributes: {
          'data-is-readonly-text': true,
          'data-pmw-type': window.PMW.emails.components.misc.PMW_READ_ONLY_FOOTER,
        },
      },
    },
    isComponent: (el) => {
      if (el instanceof Element && el.getAttribute('data-is-readonly-text')) {
        return {
          type: window.PMW.emails.components.misc.PMW_READ_ONLY_FOOTER,
        } as ComponentDefinitionDefined;
      }

      return false;
    },
  });

  editor.DomComponents.addType('script', {
    isComponent: () => false,
  });

  editor.DomComponents.addType('iframe', {
    isComponent: () => false,
  });
};
